import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity } from 'lucide-react';
import { TEAM_AVATARS } from './teamAvatars';
import { useT } from '../i18n';

/* Simulated live operations feed — new events stream in automatically. */

const EVENTS = [
  { a: 'rachel', en: 'confirmed a table for 4 via WhatsApp', es: 'confirmó una mesa para 4 por WhatsApp' },
  { a: 'rachel', en: 'replied to a new 5-star Google review', es: 'respondió una nueva reseña de 5 estrellas en Google' },
  { a: 'penny', en: 'published a new visibility article', es: 'publicó un nuevo artículo de visibilidad' },
  { a: 'sonny', en: 'pushed today\'s special to Google Business', es: 'publicó el especial de hoy en Google Business' },
  { a: 'stan', en: 'qualified a corporate catering lead', es: 'calificó un lead de catering corporativo' },
  { a: 'eva', en: 'closed the weekly KPI report', es: 'cerró el reporte semanal de KPIs' },
  { a: 'linda', en: 'reviewed a supplier agreement', es: 'revisó un contrato de proveedor' },
  { a: 'rachel', en: 'rescued a delivery complaint before it became a 1-star', es: 'rescató una queja de delivery antes de ser 1 estrella' },
  { a: 'sonny', en: 'scheduled the weekend campaign', es: 'programó la campaña del fin de semana' },
  { a: 'stan', en: 'booked a discovery call with an office manager', es: 'agendó una discovery call con una office manager' },
];

const ago = (sec, isEs) => {
  if (sec < 60) return isEs ? `hace ${sec}s` : `${sec}s ago`;
  const m = Math.floor(sec / 60);
  return isEs ? `hace ${m} min` : `${m} min ago`;
};

export default function LiveActivityFeed() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [items, setItems] = useState(() =>
    [0, 3, 4].map((i, k) => ({ id: k, ev: EVENTS[i], t: Date.now() - (k + 1) * 47000 }))
  );
  const [, force] = useState(0);

  useEffect(() => {
    const add = setInterval(() => {
      setItems((prev) => {
        const pool = EVENTS.filter((e) => !prev.slice(0, 3).some((p) => p.ev === e));
        const ev = pool[Math.floor(Math.random() * pool.length)];
        return [{ id: Date.now(), ev, t: Date.now() }, ...prev].slice(0, 5);
      });
    }, 5500);
    const tick = setInterval(() => force((n) => n + 1), 1000);
    return () => { clearInterval(add); clearInterval(tick); };
  }, []);

  return (
    <section className="bg-[#0B0D12] pb-20 lg:pb-24" data-testid="live-activity-feed">
      <div className="max-w-[880px] mx-auto px-6 lg:px-10">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
          <div className="flex items-center justify-between gap-3">
            <p className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-[#FFB672]">
              <Activity className="w-4 h-4" /> {isEs ? 'Actividad del equipo' : 'Team activity'}
            </p>
            <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
            </span>
          </div>
          <div className="mt-5 space-y-2.5 overflow-hidden">
            <AnimatePresence initial={false}>
              {items.map(({ id, ev, t }) => {
                const av = TEAM_AVATARS.find((x) => x.key === ev.a);
                const sec = Math.max(1, Math.floor((Date.now() - t) / 1000));
                return (
                  <motion.div
                    key={id}
                    initial={{ opacity: 0, y: -14, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4 }}
                    className="overflow-hidden"
                  >
                    <div className="flex items-center gap-3 rounded-xl bg-white/[0.04] border border-white/[0.07] px-3.5 py-2.5">
                      <img src={av?.img} alt={av?.name} className="w-8 h-8 rounded-full object-cover ring-1 ring-white/20 shrink-0" />
                      <p className="text-[13px] text-neutral-300 min-w-0 truncate">
                        <span className="font-semibold text-white">{av?.name}</span> {isEs ? ev.es : ev.en}
                      </p>
                      <span className="ml-auto text-[10.5px] text-neutral-500 shrink-0 whitespace-nowrap">{ago(sec, isEs)}</span>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
          <p className="mt-4 text-[10.5px] text-neutral-500">
            {isEs ? 'Ejemplo ilustrativo de un día típico gestionado por KITXENS.' : 'Illustrative example of a typical day managed by KITXENS.'}
          </p>
        </div>
      </div>
    </section>
  );
}
